import React, { MouseEvent } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormSize, FormThemeProvider } from "../ecosystems/forms";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface DialogProps {
  show: boolean;
  className?: string;
  alert?: boolean;
  dialogClassName?: string;
  title?: React.ReactNode | undefined;
  message?: ((props: { cancel(): void }) => React.ReactNode) | React.ReactNode;
  confirmDisabled?: boolean;
  confirmText?: React.ReactNode;
  cancelText?: React.ReactNode;
  confirmType?: { alert: boolean };
  onConfirm(): void;
  onCancel(event?: MouseEvent<HTMLButtonElement>): void;
  maxWidth?: string;
}
export function ConfirmModal(props: DialogProps): JSX.Element {
  const {
    confirmDisabled = false,
    message = "Are you sure?",
    confirmText = "Confirm",
    cancelText = "Cancel",
    confirmType,
  } = props;

  const handleHide = () => {
    props.onCancel();
  };

  const renderMessage = () => {
    if (!props.title) return null;
    if (typeof message === "function") {
      return message({ cancel: handleHide });
    }
    return message;
  };

  // For title in DialogTitle, only show if it's not a function
  const titleContent =
    props.title ?? (typeof message !== "function" ? message : "Confirm");

  // Detect if this is a destructive action based on confirmText
  const isDestructive =
    confirmType?.alert ||
    (typeof confirmText === "string" &&
      (confirmText.toLowerCase().includes("remove") ||
        confirmText.toLowerCase().includes("delete")));

  return (
    <Dialog open={props.show} modal={true}>
      <DialogContent
        showCloseButton={false}
        className={cn(
          "max-w-md border-neutral-200",
          props.className,
          props.dialogClassName
        )}
        onPointerDownOutside={(e) => {
          // Prevent any outside interactions
          e.preventDefault();
          e.stopPropagation();
        }}
        onInteractOutside={(e) => {
          // Prevent closing when clicking outside
          e.preventDefault();
          e.stopPropagation();
        }}
        onEscapeKeyDown={(e) => {
          // Prevent closing on escape key
          e.preventDefault();
          e.stopPropagation();
        }}
        onOpenAutoFocus={(e) => {
          // Prevent default focus behavior that might trigger parent
          e.preventDefault();
        }}
        onClick={(e) => {
          // Stop clicks from bubbling to parent modal
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          // Stop mouse down from bubbling to parent modal
          e.stopPropagation();
        }}
      >
        <DialogHeader>
          <DialogTitle className="font-gotham text-xl font-semibold text-neutral-900">
            {titleContent}
          </DialogTitle>
        </DialogHeader>

        <div className="font-inter text-sm text-neutral-700">
          {renderMessage()}
        </div>

        <div className="flex flex-row justify-between items-center pt-4 pb-3">
          <FormThemeProvider size={FormSize.large}>
            <Button
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                handleHide();
              }}
              className="border-neutral-200 hover:border-neutral-300"
            >
              {cancelText}
            </Button>
          </FormThemeProvider>
          <FormThemeProvider size={FormSize.large}>
            <Button
              autoFocus
              variant={isDestructive ? "red-primary" : "blue-primary"}
              onClick={(e) => {
                e.stopPropagation();
                props.onConfirm();
              }}
              disabled={confirmDisabled}
            >
              {confirmText}
            </Button>
          </FormThemeProvider>
        </div>
      </DialogContent>
    </Dialog>
  );
}
